import React, { useState, useEffect } from 'react';
import NavigationHeader from '../../components/ui/NavigationHeader';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import AdvancedFilterPanel from './components/AdvancedFilterPanel';
import DynamicVisualizationChart from './components/DynamicVisualizationChart';
import InsightSummaryPanel from './components/InsightSummaryPanel';
import ConfigurableReportBuilder from './components/ConfigurableReportBuilder';

const BusinessIntelligenceReportingDashboard = () => {
  const [filters, setFilters] = useState({
    period: 'month',
    comparison: 'previous_period',
    segments: ['all'],
    template: 'executive_summary'
  });
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(2025, 7, 1), // August 1, 2025
    end: new Date(2025, 7, 29),  // August 29, 2025
    preset: 'month'
  });

  // Executive KPIs data
  const executiveKPIs = [
    {
      title: 'Taxa de Crescimento',
      value: '48.2%',
      change: '+12.5%',
      changeType: 'positive',
      benchmark: {
        value: '35%',
        difference: '+13.2%',
        status: 'above'
      },
      icon: 'TrendingUp',
      description: 'Crescimento da receita vs período anterior',
      trend: [32, 35, 38, 42, 45, 48]
    },
    {
      title: 'CAC (Custo de Aquisição)',
      value: 'R$ 45,80',
      change: '-8.3%',
      changeType: 'positive',
      benchmark: {
        value: 'R$ 52,00',
        difference: '-R$ 6,20',
        status: 'above'
      },
      icon: 'Users',
      description: 'Custo médio para adquirir novos clientes',
      trend: [58, 55, 52, 48, 46, 45]
    },
    {
      title: 'Lifetime Value (LTV)',
      value: 'R$ 1.250',
      change: '+15.7%',
      changeType: 'positive',
      benchmark: {
        value: 'R$ 1.100',
        difference: '+R$ 150',
        status: 'above'
      },
      icon: 'DollarSign',
      description: 'Valor médio do cliente ao longo do tempo',
      trend: [1080, 1120, 1160, 1200, 1225, 1250]
    },
    {
      title: 'Participação de Mercado',
      value: '15.5%',
      change: '+2.3%',
      changeType: 'positive',
      benchmark: {
        value: '12%',
        difference: '+3.5%',
        status: 'above'
      },
      icon: 'Target',
      description: 'Share no mercado local de panificação',
      trend: [12, 12.5, 13.2, 14.1, 14.8, 15.5]
    },
    {
      title: 'Índice de Eficiência',
      value: '89.2%',
      change: '+11.4%',
      changeType: 'positive',
      benchmark: {
        value: '75%',
        difference: '+14.2%',
        status: 'above'
      },
      icon: 'Zap',
      description: 'Eficiência operacional geral',
      trend: [78, 80, 82, 85, 87, 89]
    }
  ];

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleReportGenerate = (reportConfig) => {
    console.log('Generating report with config:', reportConfig);
    // Here you would typically send the config to your backend
  };

  useEffect(() => {
    document.title = 'Business Intelligence - Di Brownie Dashboard';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader 
        onDateChange={handleDateChange}
        currentDateRange={dateRange}
        notificationCount={3}
        onToggleSidebar={() => {}}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading-bold text-foreground mb-2">
                  Business Intelligence
                </h1>
                <p className="text-muted-foreground font-body-normal">
                  Análise estratégica e insights para tomada de decisão executiva
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right text-sm">
                  <p className="text-muted-foreground">Última atualização</p>
                  <p className="font-body-medium text-foreground">29/08/2025 10:05</p>
                </div>
                <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <AdvancedFilterPanel
            onFiltersChange={handleFiltersChange}
            currentFilters={filters}
            isCollapsed={isFilterCollapsed}
            onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
          />

          {/* Executive Summary KPIs */}
          <div className="mb-8">
            <h2 className="text-xl font-heading-semibold text-foreground mb-4">
              Indicadores Estratégicos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {executiveKPIs?.map((kpi, index) => (
                <ExecutiveSummaryCard
                  key={index}
                  title={kpi?.title}
                  value={kpi?.value}
                  change={kpi?.change}
                  changeType={kpi?.changeType}
                  benchmark={kpi?.benchmark}
                  icon={kpi?.icon}
                  description={kpi?.description}
                  trend={kpi?.trend}
                />
              ))}
            </div>
          </div>

          {/* Main Analysis Area */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
            {/* Dynamic Visualization Chart */}
            <div className="lg:col-span-9">
              <DynamicVisualizationChart
                title="Análise Multidimensional de Performance"
                height={500}
                showControls={true}
              />
            </div>

            {/* Insights Panel */}
            <div className="lg:col-span-3">
              <InsightSummaryPanel />
            </div>
          </div>

          {/* Report Builder */}
          <div className="mb-8">
            <h2 className="text-xl font-heading-semibold text-foreground mb-4">
              Construtor de Relatórios Personalizados
            </h2>
            <ConfigurableReportBuilder
              onReportGenerate={handleReportGenerate}
            />
          </div>

          {/* Statistical Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6 card-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading-semibold text-lg text-card-foreground">
                  Significância Estatística
                </h3>
                <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                  <span className="text-success font-heading-bold text-sm">✓</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Nível de Confiança:</span>
                  <span className="font-body-medium text-success">95%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">P-valor:</span>
                  <span className="font-body-medium text-card-foreground">&lt; 0.001</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Margem de Erro:</span>
                  <span className="font-body-medium text-card-foreground">±2.3%</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6 card-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading-semibold text-lg text-card-foreground">
                  Modelagem Preditiva
                </h3>
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-primary font-heading-bold text-sm">AI</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Acurácia do Modelo:</span>
                  <span className="font-body-medium text-primary">91.2%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">R² Score:</span>
                  <span className="font-body-medium text-card-foreground">0.89</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Próxima Atualização:</span>
                  <span className="font-body-medium text-card-foreground">6h</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6 card-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading-semibold text-lg text-card-foreground">
                  Qualidade dos Dados
                </h3>
                <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                  <span className="text-warning font-heading-bold text-sm">!</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Completude:</span>
                  <span className="font-body-medium text-success">98.7%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Consistência:</span>
                  <span className="font-body-medium text-warning">94.2%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Atualização:</span>
                  <span className="font-body-medium text-success">Tempo Real</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Information */}
          <div className="bg-card rounded-lg border border-border p-6 card-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-primary font-heading-bold">BI</span>
                </div>
                <div>
                  <h3 className="font-heading-semibold text-card-foreground">
                    Sistema de Business Intelligence
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Processamento noturno de dados • Overlay em tempo real para métricas críticas
                  </p>
                </div>
              </div>
              
              <div className="text-right text-sm text-muted-foreground">
                <p>Próximo processamento: 30/08/2025 02:00</p>
                <p>Fonte de dados: ERP + POS + CRM integrados</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BusinessIntelligenceReportingDashboard;